import { useQuery } from "@tanstack/react-query";
import { planApi } from "./axios";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";
import { DataI, ServiceResponseI, PlanI, GetPlanParamsI } from "../../interfaces";

const getPlans = async ({
  pagination,
  sorting,
  globalFilter,
}: {
  pagination: { pageIndex: number; pageSize: number };
  sorting: any[];
  globalFilter: string;
}): Promise<ServiceResponseI<DataI<PlanI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));

    const { data } = await planApi.get<ServiceResponseI<DataI<PlanI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const planById = async (id: string): Promise<ServiceResponseI<PlanI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await planApi.get<ServiceResponseI<PlanI>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const planPost = async <T>(payload: T): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await planApi.post<ServiceResponseI<string>>("", payload, {
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const planPut = async <T>(id: string, payload: T): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await planApi.put<ServiceResponseI<string>>(`/${id}`, payload, {
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const planDelete = async (id: string): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await planApi.delete<ServiceResponseI<string>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const planSetStatus = async (id: string): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await planApi.patch<ServiceResponseI<string>>(
      `/set-status/${id}`,
      {},
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllPlansNoPagination = async (): Promise<ServiceResponseI<PlanI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await planApi.get<ServiceResponseI<PlanI[]>>(
      "/all-no-pagination",
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const usePlans = ({
  pagination,
  sorting,
  globalFilter,
}: {
  pagination: { pageIndex: number; pageSize: number };
  sorting: any[];
  globalFilter: string;
}) => {
  const plansQuery = useQuery(
    ["plans", { pagination, sorting, globalFilter }],
    () => getPlans({ pagination, sorting, globalFilter }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { plansQuery };
};